// src/pages/admin/AdminUserDetail.jsx
//
// Founder-only user detail page at /admin/users/:uid. Click-through from
// the /admin/users engagement table. Shows profile, teams, and the
// recent admin actions THE USER performed (adminAudit where actorUid
// matches).
//
// See features/admin-user-detail/01-spec.md for the approved scope.

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { usePlatformClaims } from '@/hooks/usePlatformClaims';
import OnboardingLadder from './components/OnboardingLadder';
import NudgeCard from './components/NudgeCard';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";
const ACTIVE_7D_MS  = 7  * 24 * 60 * 60 * 1000;
const ACTIVE_30D_MS = 30 * 24 * 60 * 60 * 1000;

function toMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.toDate === 'function')   return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  return Number(ts) || 0;
}

function relativeAgo(ms) {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return 'in the future';
  const mins = Math.floor(diff / 60000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30)  return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function fmtDateTime(ts) {
  const ms = toMs(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function displayName(u) {
  const full = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  if (full) return full;
  if (u.displayName) return u.displayName;
  if (u.email) return u.email.split('@')[0];
  return u.id?.slice(0, 8) || 'Unknown';
}

function lastSeenMs(u) {
  return Math.max(
    toMs(u.presence?.lastSeenAt),
    toMs(u.lastActive),
  );
}

function healthBadge(u) {
  const last = lastSeenMs(u);
  if (!last) return { tone: 'never',   label: '⚪ Never' };
  const age = Date.now() - last;
  if (age < ACTIVE_7D_MS)  return { tone: 'active',  label: '🟢 Active' };
  if (age < ACTIVE_30D_MS) return { tone: 'stale',   label: '🟡 Stale' };
  return                          { tone: 'dormant', label: '🔴 Dormant' };
}

function onboardingBadge(u) {
  if (u.onboardingComplete === true) return { tone: 'done', label: '✅ Onboarded' };
  const status = (u.onboardingStatus || '').toLowerCase();
  if (status === 'completed')         return { tone: 'done', label: '✅ Onboarded' };
  if (status === 'registered')        return { tone: 'starting', label: '⏳ Registered' };
  if (status === 'in_progress')       return { tone: 'starting', label: '⏳ In progress' };
  return { tone: 'unknown', label: '— —' };
}

const HEALTH_CLASS = {
  active:  'bg-lime-100 text-lime-800 border-lime-200',
  stale:   'bg-amber-100 text-amber-800 border-amber-200',
  dormant: 'bg-red-100 text-red-800 border-red-200',
  never:   'bg-zinc-100 text-zinc-600 border-zinc-200',
};

const ONBOARDING_CLASS = {
  done:     'bg-lime-100 text-lime-800 border-lime-200',
  starting: 'bg-amber-100 text-amber-800 border-amber-200',
  unknown:  'bg-zinc-100 text-zinc-600 border-zinc-200',
};

// Collect a user's team-id list across the three possible array fields.
function userTeamIds(u) {
  const set = new Set();
  for (const arr of [u.teamIds, u.teams, u.memberships]) {
    if (Array.isArray(arr)) {
      for (const v of arr) {
        if (typeof v === 'string' && v) set.add(v);
        else if (v && typeof v === 'object' && typeof v.teamId === 'string') set.add(v.teamId);
        else if (v && typeof v === 'object' && typeof v.id === 'string') set.add(v.id);
      }
    }
  }
  return [...set];
}

function summarizeAudit(a) {
  if (a.action === 'setClubTier') {
    const before = a.before || {}, after = a.after || {};
    const parts = [];
    for (const f of ['tier', 'foundingClubNumber', 'trialEndsAt', 'seatLimit']) {
      const b = renderForFeed(before[f]);
      const af = renderForFeed(after[f]);
      if (b !== af) parts.push(`${labelOf(f)}: ${b ?? '—'} → ${af ?? '—'}`);
    }
    return parts.length === 0 ? 'No-op tier write' : parts.join(' · ');
  }
  if (a.action === 'setRegistrationContact') {
    const before = a.before || {}, after = a.after || {};
    const parts = [];
    for (const f of ['parentName', 'parentPhone', 'parentEmail', 'language']) {
      const b = renderForFeed(before[f]);
      const af = renderForFeed(after[f]);
      if (b !== af) parts.push(`${labelOf(f)}: ${b ?? '—'} → ${af ?? '—'}`);
    }
    return parts.length === 0 ? 'No-op registration write' : parts.join(' · ');
  }
  return a.action || 'admin action';
}

function labelOf(f) {
  switch (f) {
    case 'tier':               return 'tier';
    case 'foundingClubNumber': return 'FC#';
    case 'trialEndsAt':        return 'trial';
    case 'seatLimit':          return 'seats';
    case 'parentName':         return 'name';
    case 'parentPhone':        return 'phone';
    case 'parentEmail':        return 'email';
    case 'language':           return 'lang';
    default: return f;
  }
}

function renderForFeed(v) {
  if (v == null) return null;
  if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate().toISOString().slice(0, 10);
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);
  return String(v);
}

export default function AdminUserDetail() {
  const { uid } = useParams();
  const { isFounder } = usePlatformClaims();
  const blocked = !isFounder;

  const [user, setUser]         = useState(null);
  const [teams, setTeams]       = useState([]);
  const [audits, setAudits]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [auditError, setAuditError] = useState(null);

  async function loadAll() {
    setLoading(true);
    setError(null);
    setAuditError(null);
    try {
      const uSnap = await getDoc(doc(db, 'users', uid));
      if (!uSnap.exists()) {
        setError('not-found');
        setLoading(false);
        return;
      }
      const u = { id: uSnap.id, ...uSnap.data() };
      setUser(u);

      // Fetch teams in parallel
      const tids = userTeamIds(u);
      if (tids.length) {
        const teamSnaps = await Promise.all(
          tids.map((tid) => getDoc(doc(db, 'teams', tid)).catch(() => null)),
        );
        const ts = teamSnaps
          .filter((s) => s && s.exists())
          .map((s) => ({ id: s.id, ...s.data() }));
        setTeams(ts);
      } else {
        setTeams([]);
      }

      // Recent admin actions performed by this user
      try {
        const aq = query(
          collection(db, 'adminAudit'),
          where('actorUid', '==', uid),
          orderBy('createdAt', 'desc'),
          limit(10),
        );
        const aSnap = await getDocs(aq);
        setAudits(aSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (ae) {
        setAuditError(ae?.message || 'Failed to load admin actions.');
      }
    } catch (e) {
      setError(e?.message || 'Failed to load user.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (blocked) return;
    if (!uid) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, blocked]);

  if (blocked) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
          <p>
            <span className="font-semibold">Insufficient privilege.</span>{' '}
            Founder claim required.
          </p>
        </div>
      </div>
    );
  }

  if (error === 'not-found') {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Link to="/admin/users" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-4">
          <ArrowLeftIcon className="h-4 w-4" />
          Back to users
        </Link>
        <div className="rounded-2xl border border-zinc-200 bg-white px-6 py-12 text-center text-zinc-600">
          This user doesn't exist.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <Link to="/admin/users" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-4">
        <ArrowLeftIcon className="h-4 w-4" />
        Back to users
      </Link>

      {loading && !user && (
        <div className="space-y-3">
          <div className="h-12 rounded-md bg-zinc-100 animate-pulse" />
          <div className="h-32 rounded-2xl bg-zinc-100 animate-pulse" />
          <div className="h-64 rounded-2xl bg-zinc-100 animate-pulse" />
        </div>
      )}

      {error && error !== 'not-found' && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold">Couldn't load this user</p>
            <p className="text-red-700">{error}</p>
          </div>
          <button
            type="button"
            onClick={loadAll}
            className="px-2 py-1 rounded text-xs font-semibold border border-red-300 text-red-800 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {user && (
        <>
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt=""
                className="h-16 w-16 rounded-full ring-1 ring-zinc-200 shrink-0"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-zinc-100 shrink-0">
                <UserCircleIcon className="h-10 w-10 text-zinc-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1
                className="text-3xl font-bold uppercase tracking-tight text-zinc-900 leading-tight"
                style={{ fontFamily: HEAD_FONT }}
              >
                {displayName(user)}
              </h1>
              <p className="text-sm text-zinc-600 truncate">{user.email || '—'}</p>
              <p className="text-[10px] text-zinc-400 font-mono truncate">{user.id}</p>
            </div>
            <button
              type="button"
              onClick={loadAll}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Badges + dates */}
          <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-4 mb-4 text-sm">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${ONBOARDING_CLASS[onboardingBadge(user).tone]}`}>
                {onboardingBadge(user).label}
              </span>
              <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${HEALTH_CLASS[healthBadge(user).tone]}`}>
                {healthBadge(user).label}
              </span>
              {user.claims?.founder === true && (
                <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-zinc-900 text-lime-300 border-zinc-800">
                  🔑 Founder
                </span>
              )}
              {user.claims?.support === true && (
                <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-indigo-100 text-indigo-800 border-indigo-200">
                  🛟 Support
                </span>
              )}
              {user.claims?.finance === true && (
                <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-emerald-100 text-emerald-800 border-emerald-200">
                  💰 Finance
                </span>
              )}
              <Link
                to="/admin/platform-claims"
                className="ml-auto text-xs font-semibold text-lime-700 hover:underline"
              >
                Manage claims →
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-zinc-700">
              <div>
                <span className="text-zinc-500">Signed up:</span>{' '}
                {fmtDateTime(user.createdAt)} <span className="text-zinc-400">({relativeAgo(toMs(user.createdAt))})</span>
              </div>
              <div>
                <span className="text-zinc-500">Last seen:</span>{' '}
                {fmtDateTime(lastSeenMs(user))} <span className="text-zinc-400">({relativeAgo(lastSeenMs(user))})</span>
              </div>
              {user.phoneNumber && (
                <div><span className="text-zinc-500">Phone:</span> <span className="font-mono text-xs">{user.phoneNumber}</span></div>
              )}
              {user.locale && (
                <div><span className="text-zinc-500">Locale:</span> {user.locale}</div>
              )}
            </div>
          </div>

          {/* Onboarding ladder + recovery nudge (features/onboarding-recovery/) */}
          <div className="grid gap-4 sm:grid-cols-2 mb-4">
            <OnboardingLadder onboarding={user.onboarding} />
            <NudgeCard uid={user.id} user={user} onSent={loadAll} />
          </div>

          {/* Teams */}
          <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-4 mb-4">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">
              Teams ({teams.length})
            </h2>
            {teams.length === 0 ? (
              <p className="text-sm text-zinc-500">Not on any teams yet.</p>
            ) : (
              <ul className="space-y-2">
                {teams.map((t) => (
                  <li key={t.id} className="border-l-2 border-lime-300 pl-3 py-1">
                    <p className="font-semibold text-zinc-900">
                      {t.name || <span className="text-zinc-400 italic">(unnamed team)</span>}
                    </p>
                    <p className="text-[10px] text-zinc-400 font-mono">{t.id}</p>
                    {t.clubId && (
                      <p className="text-xs text-zinc-500 mt-0.5">
                        Club:{' '}
                        <Link
                          to={`/admin/clubs/${t.clubId}`}
                          className="text-lime-700 hover:underline font-mono"
                        >
                          {t.clubId}
                        </Link>
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Recent admin actions by this user */}
          <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-4">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">
              Recent admin actions by this user (last 10)
            </h2>

            {auditError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 flex items-start gap-2 mb-3">
                <ExclamationTriangleIcon className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                <span>{auditError}</span>
              </div>
            )}

            {!auditError && audits.length === 0 && (
              <p className="text-sm text-zinc-500">This user has never performed an admin action.</p>
            )}

            {audits.length > 0 && (
              <ul className="space-y-3 text-sm">
                {audits.map((a) => {
                  const isClub = a.targetType === 'club' && a.targetId;
                  const isReg  = a.targetType === 'registration' && a.targetId;
                  const inner = (
                    <div>
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-lime-500" />
                        <span>{fmtDateTime(a.createdAt)}</span>
                        <span className="text-zinc-400">·</span>
                        <span>{a.action}</span>
                      </div>
                      <p className="mt-1 text-zinc-800">{summarizeAudit(a)}</p>
                      {a.reason && (
                        <p className="mt-1 text-xs text-zinc-500">"{a.reason}"</p>
                      )}
                    </div>
                  );
                  return (
                    <li key={a.id} className="border-l-2 border-lime-300 pl-3">
                      {isClub ? (
                        <Link to={`/admin/clubs/${a.targetId}`} className="block hover:underline">
                          {inner}
                        </Link>
                      ) : isReg ? (
                        <span className="block">{inner}</span>
                      ) : (
                        <span className="block">{inner}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
